import { OCEAN_API_URL } from '../shared/constants/env';

function getApiBase(): string {
  return OCEAN_API_URL;
}

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function apiRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${getApiBase()}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok) throw new Error(json?.message || `${method} ${path} failed (${res.status})`);
  return json as T;
}

export interface TviklaCarrier {
  id: string;
  name: string;
  features: string[];
  booking_via_api: boolean;
}

export async function fetchCarriers(): Promise<TviklaCarrier[]> {
  const data = await apiRequest<{ data: TviklaCarrier[] }>('GET', '/api/v1/ocean/carriers');
  return data?.data ?? [];
}

export interface ScheduleOptions {
  origin_port: string;
  destination_port: string;
  departure_date: string;
  carrier?: string;
}

export interface RawSchedule {
  departureDate: string;
  arrivalDate: string;
  transitTime: number;
  legs: number;
  originPort: string;
  destinationPort: string;
  carrier: string;
}

export async function fetchSchedules(opts: ScheduleOptions): Promise<RawSchedule[]> {
  const data = await apiRequest<any>('POST', '/api/v1/ocean/schedules', opts);
  return (data?.data?.schedules ?? []) as RawSchedule[];
}

export interface CmaVessel {
  imo: string;
  name: string;
  teuCapacity: number;
  status: string;
  onFleet: boolean;
}

export async function resolveLocode(location: string, fallback: string): Promise<string> {
  if (!location) return fallback;
  try {
    const data = await apiRequest<any>('GET', `/api/v1/ocean/resolve-locode?location=${encodeURIComponent(location)}`);
    return data?.data?.locode ?? fallback;
  } catch {
    return fallback;
  }
}

export async function fetchCmaVessels(nameContains = 'CMA'): Promise<CmaVessel[]> {
  const data = await apiRequest<any>(
    'GET',
    `/api/v1/ocean/cma-cgm/vessels?name_contains=${encodeURIComponent(nameContains)}`,
  );
  return (data?.data ?? []).map((v: any) => ({
    imo: v.imo as string,
    name: v.name as string,
    teuCapacity: (v.teuCapacity as number) ?? 0,
    status: (v.status as string) ?? '',
    onFleet: (v.onFleet as boolean) ?? false,
  }));
}

// Indicative rates — pricing APIs are not live yet
const CARRIER_BASE_RATES: Record<string, number> = {
  'maersk': 1450,
  'hapag-lloyd': 1380,
  'cma-cgm': 1290,
};

const CARRIER_DEMURRAGE: Record<string, number> = {
  'maersk': 14,
  'hapag-lloyd': 10,
  'cma-cgm': 7,
};

const CARRIER_DISPLAY_NAMES: Record<string, string> = {
  'maersk': 'Maersk',
  'hapag-lloyd': 'Hapag-Lloyd',
  'cma-cgm': 'CMA CGM',
};

// Fallback transit days when a carrier's schedule API is unavailable
const CARRIER_FALLBACK_TRANSIT: Record<string, number> = {
  'maersk': 28,
  'hapag-lloyd': 30,
  'cma-cgm': 26,
};

export interface MappedQuote {
  id: string;
  carrier: string;
  etaDays: number;
  cost: number;
  transit: string;
  freeDemurrageDays: number;
  estimatedDeparture?: string;
  estimatedArrival?: string;
  liveSchedule: boolean; // true = real API data, false = indicative fallback
}

export function schedulesToQuotes(schedules: RawSchedule[]): MappedQuote[] {
  const byCarrier = new Map<string, RawSchedule[]>();
  for (const s of schedules) {
    const key = s.carrier.toLowerCase();
    if (!byCarrier.has(key)) byCarrier.set(key, []);
    byCarrier.get(key)!.push(s);
  }

  const quotes: MappedQuote[] = [];

  // Always include all known carriers — use live data when available, fallback otherwise
  for (const carrierId of Object.keys(CARRIER_BASE_RATES)) {
    const carrierSchedules = byCarrier.get(carrierId);
    const baseRate = CARRIER_BASE_RATES[carrierId];

    if (carrierSchedules && carrierSchedules.length > 0) {
      const best = carrierSchedules.reduce((a, b) => (a.transitTime < b.transitTime ? a : b));
      const transitFactor = best.transitTime > 30 ? 0.9 : best.transitTime > 20 ? 1.0 : 1.1;
      quotes.push({
        id: carrierId,
        carrier: CARRIER_DISPLAY_NAMES[carrierId] ?? carrierId,
        etaDays: best.transitTime,
        cost: Math.round(baseRate * transitFactor),
        transit: best.legs === 1 ? 'Direct' : `Sea (${best.legs} legs)`,
        freeDemurrageDays: CARRIER_DEMURRAGE[carrierId] ?? 7,
        estimatedDeparture: best.departureDate,
        estimatedArrival: best.arrivalDate,
        liveSchedule: true,
      });
    } else {
      // No live schedule — show carrier with indicative pricing and typical transit
      const transitDays = CARRIER_FALLBACK_TRANSIT[carrierId] ?? 28;
      quotes.push({
        id: carrierId,
        carrier: CARRIER_DISPLAY_NAMES[carrierId] ?? carrierId,
        etaDays: transitDays,
        cost: baseRate,
        transit: 'Sea',
        freeDemurrageDays: CARRIER_DEMURRAGE[carrierId] ?? 7,
        liveSchedule: false,
      });
    }
  }

  return quotes.sort((a, b) => a.cost - b.cost);
}

export interface MappedVessel {
  id: string;
  name: string;
  eta: string;
  capacity: string;
  containerCount: number;
  availableContainers: number;
  nextDeparture: string;
}

export function schedulesToVessels(
  schedules: RawSchedule[],
  carrierId: string,
  cmaVessels?: CmaVessel[],
): MappedVessel[] {
  const vesselPool = cmaVessels ?? [];
  const displayName = CARRIER_DISPLAY_NAMES[carrierId] ?? carrierId;
  const transitDays = CARRIER_FALLBACK_TRANSIT[carrierId] ?? 28;

  if (schedules.length === 0) {
    const departureDate = new Date();
    departureDate.setDate(departureDate.getDate() + 7);
    const arrivalDate = new Date(departureDate);
    arrivalDate.setDate(arrivalDate.getDate() + transitDays);
    return [{
      id: `${carrierId}-fallback`,
      name: `${displayName} Service`,
      nextDeparture: departureDate.toISOString().split('T')[0],
      eta: arrivalDate.toISOString().split('T')[0],
      capacity: 'N/A',
      containerCount: 0,
      availableContainers: 20,
    }];
  }

  return schedules.map((s, i) => {
    const vessel = carrierId === 'cma-cgm' && vesselPool.length > 0
      ? vesselPool[i % vesselPool.length]
      : undefined;
    const name = vessel
      ? vessel.name
      : `${displayName} Service${schedules.length > 1 ? ` ${i + 1}` : ''}`;
    const teu = vessel?.teuCapacity ?? 0;
    return {
      id: `${carrierId}-sched-${i}`,
      name,
      nextDeparture: s.departureDate.split('T')[0],
      eta: s.arrivalDate.split('T')[0],
      capacity: teu > 0 ? `${teu.toLocaleString()} TEU` : 'N/A',
      containerCount: teu,
      availableContainers: teu > 0 ? Math.max(1, Math.floor(teu * 0.15)) : 20,
    };
  });
}
